import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  Heading,
  PrimaryFullScreenSpinner,
  toast,
  useAppForm,
} from "@repo/ui";
import { dateSchema, formatRate, NotFoundError, neverFunc } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { compareDesc } from "date-fns";
import type Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense, useState } from "react";
import { z } from "zod";
import { EditRatesModal } from "~/components/Product/EditRatesModal";
import {
  type BankAccountRateFlattened,
  flattenBankAccountRate,
} from "~/components/Product/RateUpdates";
import { graphql } from "~/gql/client";
import type { PossibleTypesResultData } from "~/gql/client/possibleTypes";
import { opsHead } from "~/head";

const formSchema = z.object({
  name: z.string().nonempty(),
  internalName: z.string().nonempty(),
  availableFrom: dateSchema,
  availableTo: dateSchema.nullish(),
  _productKind: z.string(),
  _termType: z.string(),
  _rateType: z.string(),
  _unitType: z.string(),
});

type FormSchema = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/products_/$productId/")({
  head: () => opsHead("Product"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { productId } = Route.useParams();
  const { data, error } = useSuspenseQuery(
    graphql(`
      query ProductIndexPage($id: ID!) {
        product(id: $id) {
          id
          name
          internalName
          availableFrom
          availableTo
          ...on BankAccountProduct {
            rate {
              id
              rate
              effectiveFromInclusive
              tiers {
                id
                rate
                minBalanceInclusive
                maxBalanceExclusive
              }
            }
          }
          ...on FundProduct {
            cnav
            rates {
              id
              effectiveToExclusive
              rate
            }
          }
          ...on TermDepositProduct {
            rateQuotes {
              id
              rate
              effectiveAt
            }
          }
          ...EditRatesModal_Product
        }
      }
    `),
    { variables: { id: productId ?? "" } },
  );

  const product = data?.product;

  if (error) {
    throw error;
  }
  if (!product) {
    throw new NotFoundError();
  }

  const [editingRate, setEditingRate] = useState(false);

  let latestRate:
    | {
        kind: "fund" | "term_deposit" | "bank_account_basic";
        rate: Decimal;
        at: DateTime;
      }
    | {
        kind: "bank_account_tiered";
        rates: BankAccountRateFlattened[];
        at: DateTime;
      };

  switch (product.__typename) {
    case "BankAccountProduct":
      latestRate = product.rate.tiers.length
        ? {
            kind: "bank_account_tiered",
            at: DateTime.fromJSDate(product.rate.effectiveFromInclusive),
            rates: flattenBankAccountRate(product.rate),
          }
        : {
            kind: "bank_account_basic",
            at: DateTime.fromJSDate(product.rate.effectiveFromInclusive),
            rate: product.rate.rate,
          };
      break;

    case "FundProduct": {
      const latestFundRate = [...product.rates]
        .sort((a, b) =>
          compareDesc(a.effectiveToExclusive, b.effectiveToExclusive),
        )
        .at(0);
      if (!latestFundRate) {
        throw new Error("expected at least 1 rate entry");
      }
      latestRate = {
        kind: "fund",
        rate: latestFundRate.rate,
        at: DateTime.fromJSDate(latestFundRate.effectiveToExclusive),
      };
      break;
    }

    case "TermDepositProduct": {
      if (product.rateQuotes.length !== 1) {
        throw new Error("expected at least 1 rate entry");
      }
      latestRate = {
        kind: "term_deposit",
        rate: product.rateQuotes[0].rate,
        at: DateTime.fromJSDate(product.rateQuotes[0].effectiveAt),
      };
      break;
    }

    default:
      neverFunc(product);
      throw new Error("not implemented");
  }

  const [updateProduct] = useMutation(
    graphql(`
      mutation UpdateProduct($input: UpdateProductInput!) {
        updateProduct(input: $input) {
          product {
            id
            name
            internalName
            availableFrom
            availableTo
          }
        }
      }
    `),
  );

  const defaultValues: FormSchema = {
    ...product,
    _productKind: productKindToFriendlyKind(product.__typename),
    _termType:
      product.__typename === "TermDepositProduct" ? "Fixed" : "Ongoing",
    _rateType: productKindToFriendlyRateKind(product.__typename),
    _unitType: product.__typename === "FundProduct" ? "Fund" : "Currency",
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit(formData) {
      const parsedData = formSchema.parse(formData.value);
      await updateProduct({
        variables: {
          input: {
            productId: product.id,
            name: parsedData.name,
            internalName: parsedData.internalName,
            availableFrom: parsedData.availableFrom,
            availableTo: parsedData.availableTo,
          },
        },
      })
        .then(() => {
          toast({ title: "Product updated", kind: "success" });
        })
        .catch(() => {
          toast({ title: "Something went wrong", kind: "error" });
        });
    },
  });

  return (
    <>
      <div className="flex flex-row items-stretch gap-6">
        <Card className="flex flex-grow gap-4 p-6">
          <div className="flex flex-1 flex-col">
            <form.AppForm>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit(e);
                }}
                className="flex h-full w-full flex-col gap-4"
              >
                <Heading size="2xl" as="h2">
                  Details
                </Heading>
                <div className="grid grid-cols-2 gap-4">
                  <form.AppField name="name">
                    {(field) => <field.InputField label="Name" />}
                  </form.AppField>
                  <form.AppField name="internalName">
                    {(field) => <field.InputField label="Internal name" />}
                  </form.AppField>
                  <form.AppField name="_productKind">
                    {(field) => (
                      <field.InputField disabled label="Product kind" />
                    )}
                  </form.AppField>
                  <form.AppField name="_termType">
                    {(field) => <field.InputField disabled label="Term type" />}
                  </form.AppField>
                  <form.AppField name="_rateType">
                    {(field) => <field.InputField disabled label="Rate type" />}
                  </form.AppField>
                  <form.AppField name="_unitType">
                    {(field) => <field.InputField disabled label="Unit type" />}
                  </form.AppField>
                  <form.AppField name="availableFrom">
                    {(field) => (
                      <field.DatePickerField label="Available from" />
                    )}
                  </form.AppField>
                  <form.AppField name="availableTo">
                    {(field) => (
                      <field.DatePickerField
                        label="Available to"
                        hideClearButton={false}
                      />
                    )}
                  </form.AppField>
                </div>
                {product.__typename === "FundProduct" && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-zinc-500">CNAV</p>
                    <p className="text-sm">{product.cnav ? "Yes" : "No"}</p>
                  </div>
                )}
                <div className="mt-auto flex flex-row justify-end">
                  <form.Subscribe selector={(f) => f.isDirty}>
                    {(isDirty) => <form.SubmitButton disabled={!isDirty} />}
                  </form.Subscribe>
                </div>
              </form>
            </form.AppForm>
          </div>
        </Card>

        <div className="flex w-[428px] flex-col gap-6">
          <Card className="flex flex-col gap-4 p-6">
            <div className="flex flex-row items-center justify-between">
              <Heading size="2xl" as="h2">
                Rates
              </Heading>
              <div className="flex flex-row gap-4">
                <Link to="rate-history">
                  <Button variant="outline">View rate history</Button>
                </Link>
                <Button
                  variant="default"
                  onClick={() => {
                    setEditingRate(true);
                  }}
                >
                  Update rates
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-4 rounded-lg bg-zinc-100 p-4">
              {(latestRate.kind === "fund" ||
                latestRate.kind === "term_deposit" ||
                latestRate.kind === "bank_account_basic") && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-zinc-600">
                    {product.__typename === "BankAccountProduct"
                      ? "Current rate"
                      : "Rate"}
                  </p>
                  <p className="text-black text-xl">
                    {formatRate(latestRate.rate)}
                  </p>
                </div>
              )}
              {latestRate.kind === "bank_account_tiered" && (
                <div className="flex flex-row gap-6">
                  {latestRate.rates.map((rate) => (
                    <div key={rate.id} className="flex flex-col gap-2">
                      <p className="text-sm text-zinc-600">
                        $
                        {rate.minBalanceInclusive.isZero()
                          ? 0
                          : rate.minBalanceInclusive.div(1_000_000).toDP(2) +
                            "m"}
                        {rate.maxBalanceExclusive
                          ? ` - $${rate.maxBalanceExclusive
                              .div(1_000_000)
                              .toDP(2)}m`
                          : "+"}
                      </p>
                      <p className="text-black text-xl">
                        {formatRate(rate.rate)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <p className="text-sm text-zinc-600">
                  {product.__typename === "BankAccountProduct"
                    ? "Effective from"
                    : "As of"}
                </p>
                <p className="text-black text-xl">
                  {latestRate.at.toLocaleString(DateTime.DATE_MED)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <EditRatesModal
        open={editingRate}
        onOpenChange={setEditingRate}
        product={product}
      />
    </>
  );
}

function productKindToFriendlyKind(
  kind: PossibleTypesResultData["possibleTypes"]["Product"][number],
): string {
  switch (kind) {
    case "BankAccountProduct":
      return "Bank account";
    case "FundProduct":
      return "Fund";
    case "TermDepositProduct":
      return "Term deposit";
  }
}

function productKindToFriendlyRateKind(
  kind: PossibleTypesResultData["possibleTypes"]["Product"][number],
): string {
  switch (kind) {
    case "BankAccountProduct":
      return "Variable";
    case "FundProduct":
      return "Floating";
    case "TermDepositProduct":
      return "Fixed";
  }
}
